import { getSidebarItem } from "../../../config/sidebarConfig";
import {
  nextPlanOf,
  OrganizationDetails,
  organizationDetailsOf,
  Plan,
  planOf,
} from "../../account/organizationDetails";
import { askedHow } from "./askedHow";
import { openPageEntry } from "./openPage";
import { defineEntry, type HowToEntry } from "./types";

const accountPage = getSidebarItem("account");
/** Account, opened at a section: #plan, #reset-password */
const openAccount = (section?: string) => ({
  label: `Open ${accountPage.label}`,
  href: section ? `${accountPage.path}#${section}` : accountPage.path,
});

/** Upgrading is through Noticeboard support. Prices are never given: there are none on the dashboard. */
const HOW_TO_UPGRADE =
  "To upgrade, contact Noticeboard support or your Noticeboard customer care representative. Prices aren't shown on the dashboard.";

export const accountEntries = [
  // There's no language setting: the dashboard is in English
  {
    id: "account.languageHowTo",
    sidebarItem: "account",
    examples: [
      "Can I change the app language to British English?",
      "How do I change the language?",
      "Can the dashboard be in Afrikaans?",
      "Is there a language setting?",
      "Can I switch to isiZulu?",
      "How do I change the language to British English?",
      "Can I use the app in another language?",
      "Where do I set the language?",
      "Can I change the spelling to British?",
      "Does Noticeboard come in other languages?",
    ],
    howTo: {
      load: async () => ({
        text: "There's no language setting: the dashboard is in English. Your Notices say exactly what you type, so British spelling stays British.",
        actions: [],
      }),
    },
  } as HowToEntry,
  openPageEntry("account", "It's where you manage your profile and account settings.", [
    "Where do I change my profile?",
    "open my profile",
    "Where do I change our logo?",
    "Show me my account settings",
    "Where are the settings?",
    "Open settings",
  ]),

  // The Plan, as Account's Organization details show it
  defineEntry<Plan>({
    id: "account.plan",
    sidebarItem: "account",
    examples: [
      "What plan are we on?",
      "Which plan do we have?",
      "What's our subscription?",
      "Are we on Premium?",
      "What tier are we on?",
      "What does our plan include?",
      "What are our plan limits?",
      "How many circles can we have?",
      "How many team members can we have?",
      "Which Noticeboard plan is this?",
      "What package are we on?",
      "our package",
    ],
    answer: {
      load: async (_, { user }) => planOf(user?.tier),
      templates: [
        ({ name, description }) => `You're on the ${name} Plan. ${description}`,
        ({ name, description }) => `Your Organization is on ${name}. ${description}`,
        ({ name, description }) => `${name}. ${description}`,
      ],
      link: () => openAccount("plan"),
    },
  }),

  // What the next Plan adds, and how to get it. Never a price.
  defineEntry<{ current: Plan; next?: Plan }>({
    id: "account.upgrade",
    sidebarItem: "account",
    examples: [
      "What do we get if we upgrade?",
      "How do I upgrade our plan?",
      "Can we upgrade?",
      "What's in the next plan?",
      "What would upgrading give us?",
      "Is there a bigger plan?",
      "How much does it cost to upgrade?",
      "What's the price of the next plan?",
      "How do we get more circles?",
      "How do we get more team members?",
      "Upgrade our plan",
      "how much is premium",
      "whats the price",
    ],
    answer: {
      load: async (_, { user }) => ({ current: planOf(user?.tier), next: nextPlanOf(user?.tier) }),
      templates: [
        ({ current, next }) =>
          next
            ? `You're on the ${current.name} Plan. Upgrading gets you the ${next.description} ${HOW_TO_UPGRADE}`
            : `You're on the ${current.name} Plan, the top one, so there's nothing to upgrade to. ${current.description}`,
        ({ current, next }) =>
          next
            ? `Your Organization is on ${current.name}. The next Plan up is the ${next.description} ${HOW_TO_UPGRADE}`
            : `Your Organization is already on the top Plan, ${current.name}. ${current.description}`,
      ],
      link: () => openAccount("plan"),
    },
  }),

  // Organization details, as Account shows them, including what isn't set
  defineEntry<OrganizationDetails>({
    id: "account.organization",
    sidebarItem: "account",
    examples: [
      "What are our Organization details?",
      "What's our Organization ID?",
      "What's our OID?",
      "What phone number is on our account?",
      "What website do we have on Noticeboard?",
      "What's our organization called?",
      "What type of organization are we?",
      "Show me our organization details",
      "What's our school's phone number on Noticeboard?",
      "What are our account details?",
      "organization settings",
    ],
    answer: {
      load: async (_, { user }) => organizationDetailsOf(user ?? {}),
      templates: [
        (d) =>
          // The description already ends with its own full stop (or "…"), so nothing is added after it here
          `${d.nameLabel}: ${d.name}. Plan: ${d.plan}. Billing: ${d.billing}. Description: ${
            d.description ?? "No description set."
          } Type: ${d.type ?? "No type set"}. Phone Number: ${d.phoneNumber ?? "No phone number set"}. Website: ${
            d.website ?? "No website set"
          }. Organization ID (OID): ${d.organizationId}.`,
      ],
      link: () => openAccount(),
    },
  }),

  defineEntry({
    id: "account.pin",
    sidebarItem: "account",
    examples: [
      "How do I change my PIN?",
      "I forgot my PIN",
      "How do I reset my PIN?",
      "Can I set a new PIN?",
      "Where do I change my 4-digit PIN?",
      "My PIN isn't working",
      "How do I get a new PIN?",
      "Who can reset my PIN?",
      "How does a Team member change their PIN?",
      "I can't remember my PIN",
      "i cant log in",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () =>
          "Team members sign in with a username and a 4-digit PIN. Account's Reset Password asks for an Admin Email, and that is not a PIN change. An administrator resets a forgotten PIN.",
        () =>
          "Your PIN is reset by an administrator. Reset Password on Account is the Admin Email flow, not a way to change the PIN.",
        () =>
          "A Creator can't change their own PIN here. Ask an administrator to reset it. Reset Password is for an Admin Email, not the PIN.",
      ],
      link: () => openAccount(),
    },
  }),

  defineEntry({
    id: "account.password",
    sidebarItem: "account",
    examples: [
      "How do I change my password?",
      "How do I reset my password?",
      "I forgot my password",
      "Change my password",
      "Where do I update my password?",
      "Can I set a new password?",
      "How do I change my login password?",
      "Reset password",
      "My password isn't working",
      "How do I get a new password?",
      ...askedHow("reset a parent's password"),
      "Where do I change my own password?",
    ],
    answer: {
      load: async (_, ctx) => ({ pinLogin: ctx.user?.accessLevel === "Creator" }),
      templates: [
        ({ pinLogin }) =>
          pinLogin
            ? "If you sign in with a Creator PIN, ask an administrator to reset it. Reset Password on Account is for an Admin Email login, not the PIN."
            : "On Account, press Reset Password at the bottom. Enter your Admin Email and press Send Reset Instructions, and you'll get an email to set a new one.",
        ({ pinLogin }) =>
          pinLogin
            ? "A Creator PIN isn't changed from Reset Password. Ask an administrator to reset the PIN. Reset Password is the Admin Email flow."
            : "Press Reset Password at the bottom of Account, then Send Reset Instructions. You'll get an email with a link to set a new password.",
      ],
      link: () => openAccount("reset-password"),
    },
  }),

  defineEntry({
    id: "account.logout",
    sidebarItem: "account",
    examples: [
      "How do I log out?",
      "How do I sign out?",
      "Where is logout?",
      "Log out",
      "Sign out",
      "How do I log off?",
      "Where do I press Logout?",
      "How do I leave my account?",
      "I want to log out",
      "Where is sign out?",
      "How do I leave the dashboard?",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "On Account, press Logout. On a phone, Logout is also in the menu.",
        () => "Logout is on Account, and in the phone menu.",
        () => "Open Account and press Logout.",
      ],
      link: () => openAccount(),
    },
  }),

  defineEntry({
    id: "account.rename",
    sidebarItem: "account",
    examples: [
      "Can I change the organization name?",
      "How do I rename the school?",
      "Can I edit the organization name?",
      "How do I change our organization name?",
      "Where do I rename the organization?",
      "Can the organization name be changed?",
      "How do I change the school name?",
      "I want to rename our organization",
      "Where is the organization name edited?",
      "Can a Creator change the organization name?",
      "rename the school",
    ],
    answer: {
      load: async () => ({}),
      templates: [
        () => "Account shows your Organization name under Organization details. I can't rename it for you. I don't have confirmed instructions for who can edit it, and the name hasn't been changed.",
        () => "The Organization name is on Account, under Organization details. I can't change it from here.",
        () => "Open Account to see the Organization name. I can't rename the Organization for you.",
      ],
      link: () => openAccount(),
    },
  }),
];
